import { TypedDocumentNode, gql } from '@apollo/client';
import { faPen } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import {
  So5LeaderboardSeasonality,
  So5LeaderboardType,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import ButtonBase from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { LineupCount } from '@sorare/core/src/atoms/icons/LineupCount';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, LabelM } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { useSportContext } from '@sorare/core/src/contexts/sport';

import { Badge } from 'components/managerTeam/Badge';
import getLineupDisplayName from 'lib/lineup/getLineupDisplayName';
import { isFixtureStarted } from 'lib/so5';

import { ManagerTeamUpdateDialog } from '../LineupButtons/ManagerTeamUpdateDialog';
import { SeasonalityLabel } from '../SeasonalityLabel';
import { LobbyLineupHeader_so5LeaderboardContender } from './__generated__/index.graphql';

const Root = styled(Horizontal)`
  width: 100%;
`;

type Props = {
  so5LeaderboardContender?: LobbyLineupHeader_so5LeaderboardContender;
  showLineupsCount?: boolean;
  showRenameButton?: boolean;
};

export const LineupHeader = ({
  so5LeaderboardContender,
  showLineupsCount,
  showRenameButton,
}: Props) => {
  const { sport } = useSportContext();
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);

  const { so5Leaderboard, so5Lineup, managerTeam } =
    so5LeaderboardContender || {};
  const { so5Fixture } = so5Leaderboard || {};

  const { draft, so5Rankings, hasLiveGames } = so5Lineup || {};
  const ranking = so5Rankings?.[0];
  const validLineup = so5Lineup && !draft;
  const fixtureStarted = so5Fixture && isFixtureStarted(so5Fixture);

  const leagueName =
    so5Leaderboard?.so5TournamentType.so5LeaderboardType.includes('GLOBAL_CAP')
      ? so5Leaderboard?.displayName
      : so5Leaderboard?.so5LeaderboardGroup.displayName;

  const isSpecialWeekly =
    so5Leaderboard?.so5TournamentType.so5LeaderboardType ===
    So5LeaderboardType.SPECIAL_WEEKLY;

  if (!so5LeaderboardContender) {
    return <Root />;
  }
  return (
    <Root gap={1}>
      <Badge
        hasLiveGames={!!hasLiveGames}
        managerTeam={managerTeam}
        ranking={fixtureStarted ? ranking : undefined}
        validLineup={!!validLineup}
        leaderboard={so5Leaderboard}
        fixtureStarted={fixtureStarted}
      />
      <Horizontal>
        <Vertical gap={0}>
          <Horizontal gap={0.5}>
            <BodyS as="h3" bold color="var(--c-neutral-1000)">
              <Ellipsis>
                {getLineupDisplayName({
                  so5Leaderboard,
                  so5Lineup,
                  managerTeam,
                  sport,
                })}
              </Ellipsis>
            </BodyS>
            {managerTeam && showRenameButton && (
              <>
                <ButtonBase onClick={() => setRenameDialogOpen(true)}>
                  <FontAwesomeIcon icon={faPen} width={12} height={12} />
                </ButtonBase>
                <ManagerTeamUpdateDialog
                  open={renameDialogOpen}
                  onClose={() => setRenameDialogOpen(false)}
                  managerTeam={managerTeam}
                />
              </>
            )}
            {ranking?.targetedRewardWithEligibility && (
              <img
                src={
                  ranking?.targetedRewardWithEligibility.reward.target
                    ?.pictureUrl
                }
                alt=""
                width="12"
                height="12"
              />
            )}
          </Horizontal>
          <Horizontal gap={0.5}>
            {[Sport.NBA, Sport.FOOTBALL].includes(sport) && (
              <LabelM>
                <SeasonalityLabel
                  seasonality={
                    so5Leaderboard?.seasonality ===
                      So5LeaderboardSeasonality.IN_SEASON || isSpecialWeekly
                      ? So5LeaderboardSeasonality.IN_SEASON
                      : So5LeaderboardSeasonality.ALL_SEASONS
                  }
                  label={
                    so5Leaderboard?.seasonality && !isSpecialWeekly
                      ? so5Leaderboard.seasonalityName
                      : leagueName
                  }
                />
              </LabelM>
            )}
            {showLineupsCount && (
              <>
                {sport === Sport.FOOTBALL && (
                  <svg
                    viewBox="0 0 2 2"
                    xmlns="http://www.w3.org/2000/svg"
                    width="2.5"
                  >
                    <circle cx="1" cy="1" r="1" fill="var(--c-neutral-600)" />
                  </svg>
                )}
                <Horizontal gap={0.5}>
                  <LineupCount fill="var(--c-neutral-600)" />
                  <LabelM color="var(--c-neutral-600)">
                    <FormattedNumber
                      value={so5Leaderboard?.so5LineupsCount || 0}
                    />
                  </LabelM>
                </Horizontal>
              </>
            )}
          </Horizontal>
        </Vertical>
      </Horizontal>
    </Root>
  );
};

LineupHeader.fragments = {
  so5LeaderboardContender: gql`
    fragment LobbyLineupHeader_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Leaderboard {
        slug
        shortDisplayName: displayName(short: true)
        seasonality
        seasonalityName
        teamsCap
        so5LineupsCount
        so5TournamentType {
          id
          so5LeaderboardType
        }
        so5Fixture {
          slug
          ...isFixtureStarted_so5Fixture
        }
        so5LeaderboardGroup {
          slug
          displayName
        }
        ...ManagerTeamBadge_so5Leaderboard
        ...getLineupDisplayName_so5Leaderboard
      }
      so5Lineup {
        id
        draft
        hasLiveGames
        name
        so5Rankings {
          id
          ranking
          targetedRewardWithEligibility {
            reward {
              target {
                slug
                pictureUrl
              }
            }
          }
          ...ManagerTeamBadge_so5Ranking
        }
        ...getLineupDisplayName_so5Lineup
      }
      managerTeam {
        id
        name
        ...ManagerTeamBadge_managerTeam
        ...ManagerTeamUpdateDialog_managerTeam
        ...getLineupDisplayName_managerTeam
      }
    }
    ${Badge.fragments.so5Leaderboard}
    ${getLineupDisplayName.fragments.so5Leaderboard}
    ${Badge.fragments.so5Ranking}
    ${getLineupDisplayName.fragments.so5Lineup}
    ${Badge.fragments.managerTeam}
    ${getLineupDisplayName.fragments.managerTeam}
    ${ManagerTeamUpdateDialog.fragments.managerTeam}
    ${isFixtureStarted.fragments.so5Fixture}
  ` as TypedDocumentNode<LobbyLineupHeader_so5LeaderboardContender>,
};
